import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import {
  collection, addDoc, getDocs,
  serverTimestamp, query, orderBy
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Sidebar from '../Shared/Sidebar';
import { v4 as uuidv4 } from 'uuid';

export default function RecordSession() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    sessionType: 'Life Skills & Chess',
    date: new Date().toISOString().split('T')[0],
    time: '14:00',
    location: '',
    topic: '',
    facilitator: '',
    notes: '',
    parentSession: false,
    parentTopic: '',
  });

  useEffect(() => {
    fetchStudents();
  }, []);

  async function fetchStudents() {
    try {
      const snap = await getDocs(
        query(
          collection(db, 'students'),
          orderBy('firstName', 'asc')
        )
      );
      const list = snap.docs.map(d => ({
        id: d.id, ...d.data()
      }));
      setStudents(list);
      // Default all to present
      const defaultAttendance = {};
      list.forEach(s => {
        defaultAttendance[s.id] = 'present';
      });
      setAttendance(defaultAttendance);
    } catch (err) {
      console.error(err);
    }
  }

  function handleChange(e) {
    const value = e.target.type === 'checkbox'
      ? e.target.checked
      : e.target.value;
    setForm({ ...form, [e.target.name]: value });
  }

  function toggleAttendance(studentId, status) {
    setAttendance(prev => ({
      ...prev,
      [studentId]: status,
    }));
  }

  function markAll(status) {
    const updated = {};
    students.forEach(s => {
      updated[s.id] = status;
    });
    setAttendance(updated);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const sessionId = `IYA-SES-${uuidv4().slice(0, 8).toUpperCase()}`;

      const presentStudents = Object.entries(attendance)
        .filter(([_, status]) => status === 'present')
        .map(([id]) => id);

      const absentStudents = Object.entries(attendance)
        .filter(([_, status]) => status === 'absent')
        .map(([id]) => id);

      const excusedStudents = Object.entries(attendance)
        .filter(([_, status]) => status === 'excused')
        .map(([id]) => id);

      await addDoc(collection(db, 'sessions'), {
        sessionId,
        ...form,
        attendance: {
          present: presentStudents,
          absent: absentStudents,
          excused: excusedStudents,
          totalPresent: presentStudents.length,
          totalAbsent: absentStudents.length,
          totalExcused: excusedStudents.length,
          totalStudents: students.length,
        },
        recordedBy: currentUser.uid,
        createdAt: serverTimestamp(),
      });

      setSuccess(
        `Session recorded! ID: ${sessionId} · ` +
        `${presentStudents.length} of ${students.length} students present`
      );

      setForm({
        sessionType: 'Life Skills & Chess',
        date: new Date().toISOString().split('T')[0],
        time: '14:00',
        location: '',
        topic: '',
        facilitator: '',
        notes: '',
        parentSession: false,
        parentTopic: '',
      });

    } catch (err) {
      setError('Something went wrong. Please try again.');
      console.error(err);
    }
    setLoading(false);
  }

  const presentCount = Object.values(attendance)
    .filter(s => s === 'present').length;
  const absentCount = Object.values(attendance)
    .filter(s => s === 'absent').length;
  const excusedCount = Object.values(attendance)
    .filter(s => s === 'excused').length;

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>
              Record Session
            </div>
            <div style={styles.pageSub}>
              Record attendance and details for
              Saturday sessions
            </div>
          </div>
          <button
            style={styles.btnOutline}
            onClick={() => navigate(-1)}
          >
            ← Back
          </button>
        </div>

        {success && (
          <div style={styles.successMsg}>{success}</div>
        )}
        {error && (
          <div style={styles.errorMsg}>{error}</div>
        )}

        <form onSubmit={handleSubmit}>

          {/* SESSION DETAILS */}
          <div style={styles.panel}>
            <div style={styles.sectionTitle}>
              Session Details
            </div>
            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Session Type *
                </label>
                <select
                  style={styles.formInput}
                  name="sessionType"
                  value={form.sessionType}
                  onChange={handleChange}
                  required
                >
                  <option>Life Skills & Chess</option>
                  <option>Parent Session — Reflective Approach</option>
                  <option>Parent Session — Microfinance</option>
                  <option>Special Activity</option>
                  <option>Community Event</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Date *
                </label>
                <input
                  style={styles.formInput}
                  name="date"
                  type="date"
                  value={form.date}
                  onChange={handleChange}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Time *
                </label>
                <input
                  style={styles.formInput}
                  name="time"
                  type="time"
                  value={form.time}
                  onChange={handleChange}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Location
                </label>
                <input
                  style={styles.formInput}
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  placeholder="e.g. Kinondoni Community Hall"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Topic Covered *
                </label>
                <input
                  style={styles.formInput}
                  name="topic"
                  value={form.topic}
                  onChange={handleChange}
                  placeholder="e.g. Teamwork and Communication"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>
                  Facilitator
                </label>
                <input
                  style={styles.formInput}
                  name="facilitator"
                  value={form.facilitator}
                  onChange={handleChange}
                  placeholder="e.g. Staff Sarah"
                />
              </div>
              <div style={{
                ...styles.formGroup,
                gridColumn: '1 / -1'
              }}>
                <label style={styles.formLabel}>
                  Session Notes
                </label>
                <textarea
                  style={{
                    ...styles.formInput,
                    height: '90px',
                    resize: 'vertical'
                  }}
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="What happened during the session..."
                />
              </div>
            </div>
          </div>

          {/* ATTENDANCE */}
          <div style={styles.panel}>
            <div style={styles.attendanceHeader}>
              <div style={styles.sectionTitle}>
                Student Attendance
              </div>
              <div style={styles.attendanceSummary}>
                <span style={styles.presentCount}>
                  {presentCount} Present
                </span>
                <span style={styles.absentCount}>
                  {absentCount} Absent
                </span>
                <span style={styles.excusedCount}>
                  {excusedCount} Excused
                </span>
              </div>
            </div>

            {/* MARK ALL BUTTONS */}
            <div style={styles.markAllRow}>
              <span style={styles.markAllLabel}>
                Mark all:
              </span>
              <button
                type="button"
                style={styles.markAllBtn}
                onClick={() => markAll('present')}
              >
                All Present
              </button>
              <button
                type="button"
                style={styles.markAllBtnRed}
                onClick={() => markAll('absent')}
              >
                All Absent
              </button>
            </div>

            {/* STUDENT LIST */}
            {students.length === 0 ? (
              <div style={styles.empty}>
                No students registered yet.
              </div>
            ) : (
              students.map(s => (
                <div key={s.id} style={styles.studentRow}>
                  <div style={styles.studentAvatar}>
                    {s.photoURL ? (
                      <img
                        src={s.photoURL}
                        alt={s.firstName}
                        style={styles.avatarImg}
                      />
                    ) : (
                      <span>
                        {s.firstName?.[0]}{s.lastName?.[0]}
                      </span>
                    )}
                  </div>
                  <div style={styles.studentInfo}>
                    <div style={styles.studentName}>
                      {s.firstName} {s.lastName}
                    </div>
                    <div style={styles.studentMeta}>
                      {s.studentId} · Class {s.class}
                    </div>
                  </div>
                  <div style={styles.attendanceBtns}>
                    {[
                      { status: 'present', label: '', color: '#14452F', bg: '#EAF3EC' },
                      { status: 'absent', label: '', color: '#C62828', bg: '#FFEBEE' },
                      { status: 'excused', label: '', color: '#E65100', bg: '#FFF3E0' },
                    ].map(btn => (
                      <button
                        key={btn.status}
                        type="button"
                        onClick={() => toggleAttendance(
                          s.id, btn.status
                        )}
                        style={{
                          ...styles.attendanceBtn,
                          background: attendance[s.id] === btn.status
                            ? btn.bg
                            : '#F9FAFB',
                          color: attendance[s.id] === btn.status
                            ? btn.color
                            : '#9CA3AF',
                          border: attendance[s.id] === btn.status
                            ? `1.5px solid ${btn.color}33`
                            : '1.5px solid #E5E7EB',
                        }}
                      >
                        {btn.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>

          <button
            type="submit"
            style={styles.submitBtn}
            disabled={loading}
          >
            {loading
              ? 'Recording...'
              : 'Record Session'}
          </button>

        </form>
      </div>
    </div>
  );
}

const styles = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F6F4EE',
  },
  main: {
    flex: 1,
    padding: '26px',
    overflowY: 'auto',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '14px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  pageSub: {
    fontSize: '12.5px',
    color: '#6B7280',
    marginTop: '3px',
  },
  btnOutline: {
    background: 'transparent',
    color: '#14452F',
    border: '1.5px solid #1D5C3C',
    padding: '10px 20px',
    borderRadius: '10px',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  successMsg: {
    background: '#EAF3EC',
    border: '1px solid #BBD8C4',
    color: '#14452F',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  errorMsg: {
    background: '#FFEBEE',
    border: '1px solid #FFCDD2',
    color: '#C62828',
    padding: '12px 16px',
    borderRadius: '10px',
    fontSize: '13px',
    marginBottom: '20px',
  },
  panel: {
    background: '#fff',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '11px',
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#6B7280',
    marginBottom: '14px',
    paddingBottom: '8px',
    borderBottom: '1px solid #F3F4F6',
    fontWeight: '600',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  formLabel: {
    fontSize: '12px',
    color: '#6B7280',
    fontWeight: '500',
  },
  formInput: {
    padding: '11px 14px',
    borderRadius: '9px',
    border: '1.5px solid #E5E7EB',
    background: '#FAF9F5',
    fontFamily: 'inherit',
    fontSize: '13.5px',
    color: '#1C1C1E',
    outline: 'none',
  },
  attendanceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '14px',
    paddingBottom: '8px',
    borderBottom: '1px solid #F3F4F6',
  },
  attendanceSummary: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  presentCount: {
    fontSize: '12px',
    color: '#14452F',
    fontWeight: '600',
  },
  absentCount: {
    fontSize: '12px',
    color: '#C62828',
    fontWeight: '600',
  },
  excusedCount: {
    fontSize: '12px',
    color: '#E65100',
    fontWeight: '600',
  },
  markAllRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  markAllLabel: {
    fontSize: '12px',
    color: '#6B7280',
  },
  markAllBtn: {
    background: '#EAF3EC',
    color: '#14452F',
    border: '1px solid #BBD8C4',
    padding: '6px 14px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  markAllBtnRed: {
    background: '#FFEBEE',
    color: '#C62828',
    border: '1px solid #FFCDD2',
    padding: '6px 14px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  empty: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '13px',
    padding: '20px 0',
  },
  studentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 0',
    borderBottom: '1px solid #F3F4F6',
  },
  studentAvatar: {
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '13px',
    fontWeight: '600',
    color: '#fff',
    flexShrink: 0,
    overflow: 'hidden',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  studentInfo: { flex: 1 },
  studentName: {
    fontSize: '13.5px',
    fontWeight: '500',
  },
  studentMeta: {
    fontSize: '11.5px',
    color: '#6B7280',
    marginTop: '1px',
  },
  attendanceBtns: {
    display: 'flex',
    gap: '6px',
  },
  attendanceBtn: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
    fontFamily: 'inherit',
  },
  submitBtn: {
    width: '100%',
    padding: '15px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    fontFamily: 'inherit',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 6px 20px rgba(20,69,47,0.3)',
    marginBottom: '30px',
  },
};